import React, { useEffect, useMemo, useState } from "react";
import { X, TrendingUp, Users, MapPin, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Site-wide social-proof strip. Rotates through real numbers from
 * /api/social-proof every 5 seconds. Fully aggregated data — no PII.
 * Dismissible for the session (sessionStorage), not permanent — we
 * still want returning users to see fresh numbers next visit.
 */

const ROTATE_MS = 5000;
const SESSION_DISMISS_KEY = "rmc_social_proof_dismissed";

export default function SocialProofStrip() {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") setDismissed(true);
    } catch {
      /* noop */
    }
    setMounted(true);
  }, []);

  useEffect(() => {
    if (dismissed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/social-proof");
        if (!cancelled) setData(data);
      } catch {
        /* silent — strip just stays hidden until next attempt */
      }
    })();
    const refresh = setInterval(async () => {
      try {
        const { data } = await api.get("/social-proof");
        if (!cancelled) setData(data);
      } catch {
        /* noop */
      }
    }, 60000);
    return () => {
      cancelled = true;
      clearInterval(refresh);
    };
  }, [dismissed]);

  const messages = useMemo(() => buildMessages(data, lang), [data, lang]);

  useEffect(() => {
    if (messages.length < 2) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % messages.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [messages.length]);

  const handleDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  if (!mounted || dismissed || messages.length === 0) return null;
  const current = messages[idx % messages.length];

  return (
    <div
      className="fixed bottom-3 left-3 z-30 hidden max-w-sm items-center gap-3 rounded-full border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur sm:flex"
      data-testid="social-proof-strip"
      role="status"
      aria-live="polite"
    >
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: "#0369A114", color: "#0369A1" }}
      >
        <current.Icon className="h-3.5 w-3.5" />
      </span>
      <span
        className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800"
        data-testid="social-proof-message"
        key={current.key}
      >
        {current.text}
      </span>
      <button
        onClick={handleDismiss}
        className="text-slate-400 hover:text-slate-700"
        aria-label={lang === "fr" ? "Fermer" : "Dismiss"}
        data-testid="social-proof-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function buildMessages(data, lang) {
  if (!data) return [];
  const msgs = [];

  // Personalized: visitor's own city always leads if we have >=2 leads there.
  const v = data.visitor || {};
  if (v.city && (v.count_in_city || 0) >= 2) {
    msgs.push({
      key: `you-${v.city}`,
      Icon: MapPin,
      text:
        lang === "fr"
          ? `${v.count_in_city} demandes récentes à ${v.city}`
          : `${v.count_in_city} recent applications from ${v.city}`,
    });
  } else if (v.city && v.country_code === "CA") {
    // Visitor is in Canada but their exact city isn't in our data yet — still
    // hyper-relevant to greet them by city.
    msgs.push({
      key: `greet-${v.city}`,
      Icon: MapPin,
      text:
        lang === "fr"
          ? `Bienvenue depuis ${v.city} — nous couvrons tout le Québec.`
          : `Welcome from ${v.city} — we cover all of Quebec.`,
    });
  }

  if ((data.approvals_this_week || 0) >= 3) {
    msgs.push({
      key: "approvals",
      Icon: TrendingUp,
      text:
        lang === "fr"
          ? `${data.approvals_this_week} dossiers reçus cette semaine`
          : `${data.approvals_this_week} files received this week`,
    });
  }
  if ((data.active_now || 0) >= 2) {
    msgs.push({
      key: "active",
      Icon: Users,
      text:
        lang === "fr"
          ? `${data.active_now} personnes sur le site en ce moment`
          : `${data.active_now} people browsing right now`,
    });
  }
  if ((data.calc_users_5m || 0) >= 2) {
    msgs.push({
      key: "calc",
      Icon: Sparkles,
      text:
        lang === "fr"
          ? `${data.calc_users_5m} personnes utilisent les calculatrices`
          : `${data.calc_users_5m} people using the calculators`,
    });
  }
  // Fallback: top city (only if it's not the same city we already featured
  // personally — avoids duplicate messages).
  if (Array.isArray(data.top_cities) && data.top_cities.length > 0) {
    const top = data.top_cities.find(
      (c) => c && c.count >= 2 && (!v.city || c.city.toLowerCase() !== v.city.toLowerCase())
    );
    if (top) {
      msgs.push({
        key: `city-${top.city}`,
        Icon: MapPin,
        text:
          lang === "fr"
            ? `${top.count} demandes récentes à ${top.city}`
            : `${top.count} recent applications from ${top.city}`,
      });
    }
  }

  return msgs;
}
